import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useNewRowPlace } from "@/hooks/use-new-row-place";
import { useCan, useCanWithoutBuilding } from "@/lib/policy";
import { useCanChangeHrEntry } from "./-components/hr-entry-access";
import { toResponsibleParty, type HrDocumentType } from "./-components/catalog";
import { DocumentTypeForm, type DocumentTypeFormValues } from "./-components/document-type-form";
import { DocumentTypeTable } from "./-components/document-type-table";

export const Route = createFileRoute("/_authenticated/_workforce/hr/document-types")({
  component: DocumentTypesPage,
});

type Editing = { mode: "create" } | { mode: "edit"; row: HrDocumentType };

const editValues = (row: HrDocumentType): DocumentTypeFormValues => ({
  name: row.name,
  description: row.description ?? "",
  defaultIsRequired: row.defaultIsRequired,
  responsibleParty: toResponsibleParty(row.responsibleParty),
  visibleToEmployee: row.visibleToEmployee,
  appliesToAllFacilities: row.appliesToAllFacilities,
  facilityIds: row.facilityIds,
});

/** The documents an employee can be asked to hold, who produces them, and which buildings ask. */
function DocumentTypesPage() {
  const { facilityId } = useFacility();
  // The catalogue is the organization's, kept by its own ids with no building named.
  const canManage = useCanWithoutBuilding("hr:manage");
  const canSeeFacilities = useCan("facility:read");
  // Each type changes with hr:manage wherever it applies; a new one goes where HR is managed.
  const canChange = useCanChangeHrEntry();
  const place = useNewRowPlace("hr:manage");
  const hrFacilities = [...place.buildings];
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [retiring, setRetiring] = useState<HrDocumentType | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const types = useQuery(
    orpc.workforce.employeeDocuments.listTypes.queryOptions({
      input: { facilityId, includeInactive },
    }),
  );
  const facilities = useQuery({
    ...orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
    enabled: canSeeFacilities,
  });
  // Read back by id, not from the list's row: a form opened from a stale page would save that stale
  // copy over the record.
  const editingId = editing?.mode === "edit" ? editing.row.id : null;
  const editingType = useQuery({
    ...orpc.workforce.employeeDocuments.getType.queryOptions({
      input: { documentTypeId: editingId ?? "" },
    }),
    enabled: editingId !== null,
  });
  const facilityOptions = facilities.data ?? [];

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.workforce.employeeDocuments.key() });

  const create = useMutation(
    orpc.workforce.employeeDocuments.createType.mutationOptions({
      onSuccess: async () => {
        toast.success("Document type created");
        setEditing(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const update = useMutation(
    orpc.workforce.employeeDocuments.updateType.mutationOptions({
      onSuccess: async () => {
        toast.success("Document type updated");
        setEditing(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const duplicate = useMutation(
    orpc.workforce.employeeDocuments.duplicateType.mutationOptions({
      onSuccess: async () => {
        toast.success("Document type duplicated");
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const retire = useMutation(
    orpc.workforce.employeeDocuments.deactivateType.mutationOptions({
      onSuccess: async () => {
        toast.success("Document type retired");
        setRetiring(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  // Retiring is the only removal and a retired type cannot be fetched, so without this one retired
  // by mistake is gone.
  const restore = useMutation(
    orpc.workforce.employeeDocuments.updateType.mutationOptions({
      onSuccess: async () => {
        toast.success("Document type restored");
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const submit = (values: DocumentTypeFormValues) => {
    const payload = {
      ...values,
      description: values.description?.trim() ? values.description : null,
    };
    if (editing?.mode === "edit") {
      update.mutate({ documentTypeId: editing.row.id, ...payload });
      return;
    }
    create.mutate(payload);
  };

  return (
    <>
      <PageHeader
        title="Document types"
        count={types.data ? types.data.length : undefined}
        meta="The reusable catalog behind every document an employee is asked for."
        actions={
          <>
            <Button variant="outline" onClick={() => setIncludeInactive((shown) => !shown)}>
              {includeInactive ? "Hide retired" : "Show retired"}
            </Button>
            {canManage ? (
              <Button onClick={() => setEditing({ mode: "create" })}>New document type</Button>
            ) : null}
          </>
        }
      />

      {types.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : types.isError ? (
        <ReadFailed
          message="Could not load document types."
          error={types.error}
          onRetry={() => void types.refetch()}
          retrying={types.isFetching}
        />
      ) : types.data.length === 0 ? (
        <EmptyState
          title="No document types yet"
          description="Add the documents this organization asks employees to hold."
        />
      ) : (
        <DocumentTypeTable
          rows={types.data}
          facilities={facilityOptions}
          canManage={canManage}
          canChange={canChange}
          duplicating={duplicate.isPending}
          onEdit={(row) => setEditing({ mode: "edit", row })}
          onDuplicate={(row) => duplicate.mutate({ documentTypeId: row.id })}
          onDeactivate={setRetiring}
          onRestore={(row) => restore.mutate({ documentTypeId: row.id, isActive: true })}
        />
      )}

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : setEditing(null))}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing?.mode === "edit" ? "Edit document type" : "New document type"}
            </DialogTitle>
            <DialogDescription>
              Define a reusable document requirement and choose which facilities ask for it.
            </DialogDescription>
          </DialogHeader>
          {editing?.mode === "create" ? (
            <DocumentTypeForm
              key="new"
              facilities={hrFacilities}
              allAllowed={place.everyBuilding}
              pending={create.isPending}
              submitLabel="Create"
              onCancel={() => setEditing(null)}
              onSubmit={submit}
              defaultValues={{
                appliesToAllFacilities: place.startAt === null && place.everyBuilding,
                facilityIds: place.startAt ? [place.startAt] : [],
              }}
            />
          ) : editingType.data ? (
            <DocumentTypeForm
              key={editingType.data.id}
              facilities={hrFacilities}
              allAllowed={place.everyBuilding}
              pending={update.isPending}
              submitLabel="Save"
              onCancel={() => setEditing(null)}
              onSubmit={submit}
              defaultValues={editValues(editingType.data)}
            />
          ) : editing ? (
            <Skeleton className="h-64 w-full" />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={retiring !== null}
        onOpenChange={(open) => (open ? null : setRetiring(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire &ldquo;{retiring?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              It leaves the catalogue and cannot be added to a template again. Requirements already
              assigned keep their own name and are not affected, and &ldquo;Show retired&rdquo;
              brings it back if this was a mistake.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep document type</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => retiring && retire.mutate({ documentTypeId: retiring.id })}
            >
              Retire document type
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
